import React, { useState, useEffect } from 'react';
import {
  KeyIcon,
  ShieldCheckIcon,
  UsersIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
// useAuth removed - not needed for permission settings
// Removed roleManagementAPI - now using centralized roles

const DynamicPermissionSettings = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [rolePermissions, setRolePermissions] = useState({});
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [syncingPermissions, setSyncingPermissions] = useState(false);
  
  // Role management is now handled in Organization Roles page

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadRolePermissions();
    }
  }, [selectedRole]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load available roles from centralized role system
      const rolesResponse = await fetch('/api/centralized-roles/dropdown', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      
      if (!rolesResponse.ok) {
        throw new Error(`Failed to fetch roles: ${rolesResponse.status}`);
      }
      
      const rolesResult = await rolesResponse.json();
      if (rolesResult.success) {
        // Transform to expected format for permission settings
        const rolesData = rolesResult.data.map(role => ({
          role: role.role_key,
          name: role.role_key,
          display_name: role.label,
          hierarchy_level: role.hierarchy_level,
          user_count: role.user_count || 0,
          is_system_role: role.role_source === 'system'
        }));
        setAvailableRoles(rolesData);
        console.log('Centralized roles loaded:', rolesData);
      } else {
        throw new Error(rolesResult.message || 'Failed to load roles');
      }

      // Menu items are now automatically synced and don't need to be displayed separately
    } catch (error) {
      console.error('Error loading initial data:', error);
      setMessage('Failed to load permission settings: ' + error.message);
      // Initialize with empty arrays to prevent map errors
      setAvailableRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async () => {
    if (!selectedRole) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/menu-permissions/roles/${selectedRole}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();
      
      // Convert to easier format for UI
      const permissionsByModule = {};
      data.modulePermissions.forEach(module => {
        permissionsByModule[module.module] = {
          ...module,
          permissions: module.permissions.reduce((acc, perm) => {
            acc[perm.action] = {
              ...perm,
              granted: perm.granted
            };
            return acc;
          }, {})
        };
      });
      
      setRolePermissions(permissionsByModule);
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setMessage('Failed to load role permissions');
    } finally {
      setLoading(false);
    }
  };

  const syncMenuPermissions = async () => {
    setSyncingPermissions(true);
    setMessage('');
    try {
      const response = await fetch('/api/menu-permissions/sync', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      
      if (response.ok) {
        setMessage('Menu permissions synced successfully');
        await loadRolePermissions(); // Reload to show new permissions
      } else {
        throw new Error('Failed to sync permissions');
      }
    } catch (error) {
      console.error('Error syncing permissions:', error);
      setMessage('Failed to sync menu permissions');
    } finally {
      setSyncingPermissions(false);
    }
  };

  const updatePermission = (module, action, granted) => {
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        permissions: {
          ...prev[module]?.permissions,
          [action]: {
            ...prev[module]?.permissions?.[action],
            granted
          }
        }
      }
    }));
  };

  const savePermissions = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      // Convert back to API format
      const permissionsToUpdate = [];
      Object.values(rolePermissions).forEach(module => {
        if (module.permissions) {
          Object.values(module.permissions).forEach(permission => {
            permissionsToUpdate.push({
              permission_id: permission.id,
              granted: permission.granted
            });
          });
        }
      });
      
      const response = await fetch(`/api/menu-permissions/roles/${selectedRole}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions: permissionsToUpdate })
      });
      
      if (response.ok) {
        setMessage(`Permissions updated successfully for ${selectedRole} role`);
      } else {
        throw new Error('Failed to update permissions');
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      setMessage('Failed to save permissions');
    } finally {
      setLoading(false);
    }
  };

  // Role management has been moved to Organization Roles page
  // This page now only handles permission assignment for existing roles

  const toggleModule = (moduleKey) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleKey)) {
      newExpanded.delete(moduleKey);
    } else {
      newExpanded.add(moduleKey);
    }
    setExpandedModules(newExpanded);
  };

  const getModulePermissionsSummary = (moduleKey) => {
    const moduleData = rolePermissions[moduleKey];
    if (!moduleData?.permissions) return { granted: 0, total: 0 };
    
    const permissions = Object.values(moduleData.permissions);
    const granted = permissions.filter(p => p.granted).length;
    return { granted, total: permissions.length };
  };

  if (loading && Object.keys(rolePermissions).length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <KeyIcon className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dynamic Permission Settings</h1>
              <p className="text-gray-600">Configure role-based permissions for sidebar menu items</p>
            </div>
          </div>
          <button
            onClick={syncMenuPermissions}
            disabled={syncingPermissions}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${syncingPermissions ? 'animate-spin' : ''}`} />
            {syncingPermissions ? 'Syncing...' : 'Sync Menu Items'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.includes('successfully') 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {message}
        </div>
      )}

      {/* Role Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <UsersIcon className="w-5 h-5 text-gray-600 mr-2" />
          Select Role to Configure
        </h2>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="block w-64 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a role...</option>
            {Array.isArray(availableRoles) && availableRoles.map(role => (
              <option key={role.role} value={role.role}>
                {role.role} ({role.user_count} users)
              </option>
            ))}
          </select>
          
          {selectedRole && (
            <button
              onClick={savePermissions}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Permissions'}
            </button>
          )}
          </div>

          {/* Unified Role Management Link */}
          <div className="flex items-center">
            <a 
              href="/organization/roles"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              Unified Role Management
            </a>
            <span className="ml-3 text-xs text-gray-500">
              Complete role and permission management in one place
            </span>
          </div>
        </div>
      </div>

      {/* Permissions Configuration */}
      {selectedRole && Object.keys(rolePermissions).length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="w-5 h-5 text-gray-600 mr-2" />
            Menu Permissions for {selectedRole} Role
          </h2>
          
          <div className="space-y-4">
            {Object.entries(rolePermissions).map(([moduleKey, moduleData]) => {
              const isExpanded = expandedModules.has(moduleKey);
              const summary = getModulePermissionsSummary(moduleKey);
              
              return (
                <div key={moduleKey} className="border border-gray-200 rounded-lg">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleModule(moduleKey)}
                  >
                    <div className="flex items-center">
                      {isExpanded ? 
                        <ChevronDownIcon className="w-5 h-5 text-gray-400 mr-2" /> :
                        <ChevronRightIcon className="w-5 h-5 text-gray-400 mr-2" />
                      }
                      <h3 className="font-medium text-gray-900">{moduleData.moduleName}</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {summary.granted}/{summary.total} permissions granted
                      </span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${summary.total > 0 ? (summary.granted / summary.total) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  {isExpanded && moduleData.permissions && (
                    <div className="border-t border-gray-200 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(moduleData.permissions).map(([action, permission]) => (
                          <label key={action} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={permission.granted}
                              onChange={(e) => updatePermission(moduleKey, action, e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                            />
                            <span className="text-sm text-gray-700 capitalize">
                              {action} {moduleData.moduleName}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Help Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-medium text-blue-800 mb-3">🎯 Unified Role Management System</h3>
        <div className="text-sm text-blue-700 space-y-2">
          <p>• <strong>New Unified System:</strong> Role creation, organizational hierarchy, and permissions are now managed in one place at <a href="/organization/roles" className="underline hover:text-blue-900 font-medium">Organization → Unified Role Management</a>.</p>
          <p>• <strong>This Page:</strong> Use this page only for fine-tuning menu permissions for existing roles. All major role operations should be done in the unified system.</p>
          <p>• <strong>Automatic Sync:</strong> Organizational roles are automatically synchronized with permission system. Changes in one place update everywhere.</p>
          <p>• <strong>Simplified Workflow:</strong> Instead of managing two separate role systems, everything is now centralized for easier administration.</p>
          <p>• <strong>Sync Menu Items:</strong> Click to detect new sidebar menu items and create permissions automatically.</p>
        </div>
        <div className="mt-4 p-3 bg-blue-100 rounded-md">
          <p className="text-xs text-blue-800 font-medium">💡 Recommendation: Use the <a href="/organization/roles" className="underline">Unified Role Management</a> system for all role-related tasks. This page is primarily for advanced permission configuration.</p>
        </div>
      </div>

      {/* Role management modals removed - now handled in Organization Roles page */}
    </div>
  );
};

export default DynamicPermissionSettings;